import { useCallback, useMemo, useState } from 'react';
import { timeAndLeaveGateway } from '../index';
import type { EscoHrExpenseClaimResponse, EscoHrExpenseWarning, EscoHrWorkforceSummaryResponse } from '../types';
import { RetryableError } from '../../../shared/RetryableError';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { EscoHrConnectionBanner, SyntheticBadge } from './escoHrWorkforceShared';
import { hasEscoHrData, humanize, useEmployeeNames, useEscoHrLoad } from './escoHrWorkforceHooks';

/** There is no expenses permission yet, so reading uses the integrations permission until a permission decision is made. */
const EXPENSES_READ = 'integrations.read';

const warningLabels: Record<EscoHrExpenseWarning, string> = {
  MissingReceipt: 'No receipt',
  DuplicateReceipt: 'Duplicate receipt',
  UnsupportedCurrency: 'Unsupported currency',
  ReductionWithoutReason: 'Reduced or rejected without a reason',
};

function money(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

interface ExpensesData {
  summary: EscoHrWorkforceSummaryResponse;
  claims: EscoHrExpenseClaimResponse[];
}

/**
 * Expense claims from escoHR, read-only. escoHR owns the claim, the receipts and the approval; EPN shows that a receipt exists, never the receipt.
 * Tax classification and the payment route are EPN decisions that are not built yet, so nothing here is a reimbursement and nothing is used in
 * payroll. A warning (no receipt, duplicate receipt, unsupported currency, an unexplained reduction) must be resolved before any later payroll step.
 */
export function EscoHrExpensesSection() {
  const can = usePermissionCheck();
  const allowed = can(EXPENSES_READ);
  const load = useCallback(async (signal: AbortSignal): Promise<ExpensesData> => {
    const [summary, claims] = await Promise.all([
      timeAndLeaveGateway.getEscoHrExpensesSummary(signal),
      timeAndLeaveGateway.listEscoHrExpenseClaims(signal),
    ]);
    return { summary, claims };
  }, []);
  const { data, error, reload } = useEscoHrLoad(allowed, load);
  const employeeName = useEmployeeNames(allowed && data !== null);

  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [onlyWarned, setOnlyWarned] = useState(false);

  const claims = data?.claims;
  const visible = useMemo(() => (claims ?? []).filter((c) =>
    (!employeeId || c.epnEmployeeId === employeeId)
    && (!status || c.status === status)
    && (!onlyWarned || c.warnings.length > 0)
    && (!search || c.categoryRef.toLowerCase().includes(search.trim().toLowerCase()))), [claims, employeeId, status, search, onlyWarned]);

  if (!allowed) {
    return <section className="card card--wide"><p className="muted">You do not have permission to view expenses from escoHR.</p></section>;
  }

  if (error) {
    return <section className="card card--wide" data-testid="escohr-expenses-error"><RetryableError message={error} onRetry={reload} /></section>;
  }

  if (!data) {
    return <section className="card card--wide" aria-busy="true"><p className="muted">Loading expenses from escoHR…</p></section>;
  }

  const employeeIds = [...new Set(data.claims.map((c) => c.epnEmployeeId))];
  const statuses = [...new Set(data.claims.map((c) => c.status))].sort();

  return (
    <div data-testid="escohr-expenses">
      <EscoHrConnectionBanner summary={data.summary} heading="escoHR Expenses" />
      {hasEscoHrData(data.summary) && (
        <section className="card card--wide" aria-label="escoHR expense claims">
          <h4>Expense claims from escoHR</h4>
          <p className="muted">
            Receipts stay in escoHR; EPN shows only that a receipt exists. Tax classification and how a claim would be paid are EPN decisions that are not built yet, so nothing here is a reimbursement or is used in payroll.
          </p>

          {data.claims.length > 0 && (
            <div className="inline-form">
              <select aria-label="Filter by employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">All employees</option>
                {employeeIds.map((id) => <option key={id} value={id}>{employeeName(id)}</option>)}
              </select>
              <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
              <input aria-label="Search category" placeholder="Search category" value={search} onChange={(e) => setSearch(e.target.value)} />
              <label><input type="checkbox" checked={onlyWarned} onChange={(e) => setOnlyWarned(e.target.checked)} /> Only claims with a warning</label>
            </div>
          )}

          <div style={{ overflowX: 'auto', contain: 'inline-size' }}>
            <table aria-label="escoHR expense claims">
              <thead>
                <tr><th>Employee</th><th>Date</th><th>Category</th><th>Submitted</th><th>Approved</th><th>Status</th><th>Receipts</th><th>Warnings</th></tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id}>
                    <td>{employeeName(c.epnEmployeeId)}{c.isSynthetic && <> <SyntheticBadge /></>}</td>
                    <td>{c.expenseDate}</td>
                    <td>{c.categoryRef}{c.miles !== null && <div className="muted">{c.miles} miles</div>}</td>
                    <td>{money(c.submittedAmount, c.currency)}</td>
                    <td>{money(c.approvedAmount, c.currency)}{c.reductionReasonCode && <div className="muted">Reason: {humanize(c.reductionReasonCode)}</div>}</td>
                    <td>{humanize(c.status)}</td>
                    <td>{c.receiptRefs.length}</td>
                    <td>{c.warnings.length === 0 ? '—' : c.warnings.map((w) => <div key={w}><strong>{warningLabels[w] ?? 'Needs review'}</strong></div>)}</td>
                  </tr>
                ))}
                {data.claims.length === 0 && <tr><td colSpan={8} className="muted">No expense claims have been received from escoHR yet.</td></tr>}
                {data.claims.length > 0 && visible.length === 0 && <tr><td colSpan={8} className="muted">No claims match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
